import AdminPageHeader from '../../components/admin/AdminPageHeader';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useModelPermission } from '../../hooks/useModelPermission';
import { axiosInstance } from '../../services/axiosConfig';

interface RegistrationConfig {
    nivel_usuario: number | null;
    configuracion_valida: boolean;
    niveles_disponibles: { id: number; nombre: string }[];
}

export default function ConfigRegistroPage() {
    const { user } = useAuth();
    const { isDark } = useTheme();
    const permission = useModelPermission('registroclienteconfig', user?.user_level_id || 0);
    const [config, setConfig] = useState<RegistrationConfig | null>(null);
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        let active = true;
        axiosInstance.get<RegistrationConfig>('configuracion-registro/')
            .then(({ data }) => {
                if (!active) return;
                setConfig(data);
                setSelected(data.configuracion_valida ? String(data.nivel_usuario) : '');
            })
            .catch(() => { if (active) setError('No se pudo cargar la configuración. Comprueba tus permisos y vuelve a intentar.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selected || !permission?.can_update || saving) return;
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const { data } = await axiosInstance.put<RegistrationConfig>('configuracion-registro/', { nivel_usuario: Number(selected) });
            setConfig(data);
            setSelected(data.configuracion_valida ? String(data.nivel_usuario) : '');
            setSuccess('Configuración guardada. Los próximos registros usarán este nivel.');
        } catch (err: any) {
            const data = err.response?.data;
            setError(data?.nivel_usuario?.[0] || data?.detail || 'No se pudo guardar la configuración.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <main className="cowork-registration-settings" data-admin-page="config-registro" style={{ padding: '24px', maxWidth: 850, margin: '0 auto', color: isDark ? 'var(--ad-ink, #f8fafc)' : '#1e293b' }}>
            <AdminPageHeader eyebrow="CONFIGURACIONES / CO MARCA GROUP" title="Registro de clientes" description="Nivel inicial de las nuevas cuentas de clientes"/>
            <div className="cowork-settings-intro"><strong>Una bienvenida consistente para tus clientes</strong><p>Elige el nivel inicial de quienes creen su cuenta desde el registro público.</p><span>Esta preferencia se aplica a nuevas cuentas. Los clientes existentes conservan su nivel.</span></div>
            {error && <p role="alert" style={{ color: isDark ? '#fca5a5' : '#b91c1c' }}>{error}</p>}
            {success && <p role="status" style={{ color: isDark ? '#86efac' : '#166534' }}>{success}</p>}
            {loading ? <p role="status">Cargando configuración…</p> : config && (
                <form onSubmit={save} style={{ padding: 24, borderRadius: 16, background: 'var(--ad-surface)', border: '1px solid var(--ad-muted, #94a3b8)' }}>
                    {!config.configuracion_valida && <p role="alert">Selecciona y guarda un nivel activo para habilitar el registro de clientes.</p>}
                    <label htmlFor="registration-level">Nivel inicial del cliente</label>
                    <select id="registration-level" value={selected} onChange={event => { setSelected(event.target.value); setSuccess(''); }} disabled={saving || !permission?.can_update} style={{ display: 'block', width: '100%', padding: 12, margin: '12px 0', color: isDark ? 'var(--ad-ink, #f8fafc)' : '#1e293b', background: 'var(--ad-surface)' }}>
                        <option value="">Selecciona un nivel</option>
                        {config.niveles_disponibles.map(level => <option key={level.id} value={level.id}>{level.nombre}</option>)}
                    </select>
                    <p>Solo se muestran niveles activos con perfil de cliente. Selecciona CLIENTES si ese es el nivel que deseas asignar.</p>
                    {!config.niveles_disponibles.length && <p role="alert">Crea o activa un nivel con perfil de cliente en Permisos antes de continuar.</p>}
                    {permission && !permission.can_update && <p>Tienes acceso de lectura. Solicita permiso de actualización para cambiar esta opción.</p>}
                    <button type="submit" disabled={!selected || saving || !permission?.can_update} style={{ padding: '12px 20px', borderRadius: 10, border: 0, background: 'var(--ad-secondary, #3564db)', color: '#fff' }} className="btn btn-primary">{saving ? 'Guardando…' : 'Guardar configuración'}</button>
                </form>
            )}
        </main>
    );
}

